import React from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';

interface CloseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const CloseModal: React.FC<CloseModalProps> = ({
  isOpen,
  onClose,
  onConfirm
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded shadow-2xl max-w-md w-full mx-auto transform transition-all">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <FaExclamationTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Unsaved Changes
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                You have unsaved changes
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
          >
            <FaTimes className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 leading-relaxed">
            If you close this window, all your new changes or data will be lost. Are you sure you want to continue?
          </p>
          
          <div className="mt-4 p-4 bg-amber-50 rounded border border-amber-200">
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Warning: Data Loss
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Any unsaved changes, form data, or new entries will be permanently lost and cannot be recovered.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded transition-colors font-medium flex items-center justify-center gap-2"
          >
            <FaExclamationTriangle className="w-4 h-4" />
            Yes, Close Anyway
          </button>
        </div>
      </div>
    </div>
  );
};